import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import sanitize from 'sanitize-html';

import { DEFAULT_EXPIRATION_DAYS, SaveActionType } from '@common/constants';
import { addDaysToDate, notifyFromStatus } from '@common/helpers';
import { setIsDirtyState } from '@common/helpers/openNewEstimate-helper';
import { useQueryStringParameters } from '@common/hooks/useQueryStringParameters/useQueryStringParameters';
import { CustomerInfo, PricedProduct } from '@common/models';
import {
    EstimateResponseMessage,
    EstimateStatus,
    SaveEstimateResponse,
    makeSaveEstimateFormData,
} from '@common/models/save-estimate-dto';
import { stringHash } from '@common/utils';
import { NotifierType, showNotification } from '@core/components';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import useEstimate from '@core/hooks/useEstimate';
import useFetchedEstimate from '@core/hooks/useFetchedEstimate';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import ImageUtils from '@core/utils/ImageUtils';
import { useLazyFetchEstimateQuery, useSaveEstimateMultiPartMutation } from '@ee-api/estimate-api/estimateService';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { usePricedProductsContext } from '@ee-context';
import { useLazyCanvasClone } from '@fabricCanvas/useLazyCanvasClone';
import {
    selectDiscountOverride,
    selectValidSmallTicketNumber,
    setDiscountOverride,
    updateCurrentEstimate,
    updateEeStatusList,
    updateIgnoreFetchedEstimate,
    updateIsSavingEstimate,
    updatePrevEstimateStatus,
} from '@redux/slices/estimateSlice';

export type PostError = SaveErrors | AxiosError | AxiosError['response'];

interface SaveEstimateReturn {
    saveEstimate: (customerInfo: CustomerInfo, saveAction: SaveActionType) => Promise<SaveEstimateResponse>;
    isSaveLoading: boolean;
    isEstimateDirty: boolean;
    getDisplayDates: (alternateExpireDate?: Date) => {
        quoteDate: Date;
        expireDate: Date;
    };
    saveResponse?: SaveEstimateResponse;
}

export enum SaveErrors {
    NO_ESTIMATE_NUMBER,
    API_ERROR,
    IME_REJECTED,
}

export enum ErrorCodes {
    BAD_ESTIMATION = 'BAD_ESTIMATION',
}

export interface ValidationError {
    errorMessage: string;
}

export interface ApiErrorResponse {
    statusCode: number;
    errorCode: string;
    errorMessage: string;
    validationErrors: ValidationError[];
}

const emptyArray = [] satisfies EstimateResponseMessage[] | PricedProduct[];

/**
 * The following are used to log _what_ has changed to consider the estimate "dirty"
 */
interface DirtyItem {
    productId: string;
    value?: string;
    fetched?: string;
    current?: string;
}

/**
 * This is a string of the values from a product used to test for isDirty
 * @param product the pricedProduct record
 */
const dirtyProductString = (product: PricedProduct) =>
    [
        product.productId,
        product.name.trim().toLowerCase(), // ignore trivial differences
        product.quantity,
        product.adjustedQty,
        product.price,
        product.priceWithoutDiscount,
    ].join();

/**
 * Generate the dirty values for each product for doing fetched/current diffs
 * @param products
 */
const _pricedProductsDirtyList = (products: PricedProduct[] | undefined): DirtyItem[] => {
    if (!products?.length) {
        return emptyArray;
    }
    return products
        .map((product) => ({
            productId: `${product.productId}:${product.name}`,
            value: dirtyProductString(product),
        }))
        .sort((a, b) => (a.productId > b.productId ? 1 : -1));
};

function useEstimateSaver(): SaveEstimateReturn {
    const { t } = useTranslation();
    const { selectedValue: selectedStore } = useSelectedStore();

    const { updateEstimateFromDesign } = useEstimate();
    const { fetchedEstimate } = useFetchedEstimate();
    const { generatePng } = useLazyCanvasClone();

    const { pricedProducts, quoteLineItems, priceTotals } = usePricedProductsContext();
    const dispatch = useAppDispatch();
    const { retailerSettings } = useRetailerSettings();

    const discountOverride = useAppSelector(selectDiscountOverride);

    const validatedTicketNumber = useAppSelector(selectValidSmallTicketNumber);
    const { locationExternalId, userToken } = useQueryStringParameters();
    const [fetchEstimate] = useLazyFetchEstimateQuery();

    const { trackEstimateSaved } = useAnalyticsEvents();

    const [
        postEstimate,
        {
            data: saveResponse,
            isError: _isSaveError,
            isLoading: isSaveLoading,
            isSuccess: _isSaveSuccess,
            error: _saveErrorResponse,
        },
    ] = useSaveEstimateMultiPartMutation();

    const fetchedProductsHash = useMemo(() => {
        return pricedProductHash(fetchedEstimate?.estimateData?.pricedProducts);
    }, [fetchedEstimate?.estimateData?.pricedProducts]);

    // // ****************************************************************************
    // /**
    //  * Utility values for determining _what_ has changed between fetching and now
    //  */
    // const fetchedProductList = useMemo(
    //     () => pricedProductsDirtyList(fetchedEstimate?.estimateData?.pricedProducts),
    //     [fetchedEstimate?.estimateData?.pricedProducts]
    // );
    // const currentProductList = useMemo(() => pricedProductsDirtyList(pricedProducts), [pricedProducts]);
    // const pricedProductsDiff = useMemo(() => {
    //     const newProducts = currentProductList.reduce((prev, item) => {
    //         if (!fetchedProductList.find((fetchedItem) => item.productId === fetchedItem.productId)) {
    //             prev.push(item);
    //         }
    //         return prev;
    //     }, [] as DirtyItem[]);
    //     const rmProducts = fetchedProductList.reduce((prev, item) => {
    //         if (!currentProductList.find((currentItem) => item.productId === currentItem.productId)) {
    //             prev.push(item);
    //         }
    //         return prev;
    //     }, [] as DirtyItem[]);
    //     const diffItems = currentProductList
    //         .reduce((prev, item) => {
    //             const fetchedItem = fetchedProductList.find((fetchedItem) => item.productId === fetchedItem.productId);
    //             if (fetchedItem && item.value !== fetchedItem?.value) {
    //                 prev.push({ productId: item.productId, current: item.value, fetched: fetchedItem?.value });
    //             }
    //             return prev;
    //         }, [] as DirtyItem[])
    //         .filter(Boolean);
    //
    //     return {
    //         newProducts,
    //         rmProducts,
    //         diffItems,
    //     };
    // }, [currentProductList, fetchedProductList]);
    //
    // useEffect(() => {
    //     if (
    //         !pricedProductsDiff.newProducts.length &&
    //         !pricedProductsDiff.rmProducts.length &&
    //         !pricedProductsDiff.diffItems.length
    //     ) {
    //         return;
    //     }
    //     console.log('==========================================');
    //     if (pricedProductsDiff.newProducts.length) {
    //         // console.log('==new items==', pricedProductsDiff.newProducts);
    //     }
    //     if (pricedProductsDiff.rmProducts.length) {
    //         console.log('==removed items==', pricedProductsDiff.rmProducts);
    //     }
    //     if (pricedProductsDiff.diffItems.length) {
    //         console.log('==diffs==', pricedProductsDiff.diffItems);
    //     }
    // }, [pricedProductsDiff]);
    // // ****************************************************************************

    const isEstimateDirty = useMemo((): boolean => {
        const currentLineItems = quoteLineItems;
        if (!currentLineItems.length && !fetchedEstimate?.estimateData?.lineItems?.length) {
            return false;
        }

        if (
            !fetchedEstimate?.estimateData ||
            fetchedEstimate?.storeId !== selectedStore?.companyId ||
            fetchedEstimate.estimateData.lineItems.length !== currentLineItems.length ||
            (discountOverride ?? 0) > 0
        ) {
            return true;
        }

        if (fetchedEstimate.estimateData.pricedProducts?.length > 0) {
            const currentHash = pricedProductHash(pricedProducts);
            return fetchedProductsHash !== currentHash;
        }

        // this is for older quotes that didn't have the pricedProducts saved, check lineItems
        const fetchedMap = new Map(
            fetchedEstimate.estimateData.lineItems.map((item) => {
                return [item.productId, item.quantity];
            })
        );

        return currentLineItems.reduce((prev, item) => {
            const fetchedQuantity = fetchedMap.get(item.productId);
            return prev || item.quantity !== fetchedQuantity;
        }, false);
    }, [
        fetchedEstimate?.estimateData,
        fetchedEstimate?.storeId,
        fetchedProductsHash,
        pricedProducts,
        quoteLineItems,
        selectedStore?.companyId,
        discountOverride,
    ]);

    /**
     * Return the dates that should be displayed when about to save a Quote.
     * - If it's a fetched estimate and no modifications have been made, return the dates from the estimate
     * - OR
     * - If it's been modified, or it's new from scratch, use today's date and today+QUOTE_VALIDITY_IN_DAYS
     * - If given an alternateExpirationDate, use the minimum of this date or the computed dates, above
     *
     * **NOTE** EZE-582 was the source for this, but there's an issue with us not actually saving
     * the dates from the client, these are DISPLAY ONLY.
     * We may show one expiration date, but the back end calculates it again, and it may be different from what we showed
     * @param alternateExpirationDate an optional alternative expiration date (generally from a promotion date)
     *     If we need to calculate new expirations, function will return the MINIMUM of the alternate or the default
     */
    const getDisplayDates = useCallback(
        (
            alternateExpirationDate?: Date
        ): {
            quoteDate: Date;
            expireDate: Date;
        } => {
            const useFetchedQuoteDate = fetchedEstimate?.estimateData && fetchedEstimate?.quoteDate;
            const useFetchedExpireDate =
                fetchedEstimate &&
                !isEstimateDirty &&
                !!fetchedEstimate.quoteDate &&
                !!fetchedEstimate.quoteExpirationDate;
            const quoteDate = useFetchedQuoteDate ? new Date(fetchedEstimate.quoteDate) : new Date();
            const expirationDays = retailerSettings ? retailerSettings.expirationDays : DEFAULT_EXPIRATION_DAYS;
            const minExpireDate = new Date(
                Math.min(Number(alternateExpirationDate ?? Infinity), Number(addDaysToDate(quoteDate, expirationDays)))
            );
            const expireDate = useFetchedExpireDate ? new Date(fetchedEstimate.quoteExpirationDate) : minExpireDate;
            return { quoteDate, expireDate };
        },
        [fetchedEstimate, isEstimateDirty, retailerSettings]
    );

    const saveEstimate = async (
        customerInfo: CustomerInfo,
        saveAction: SaveActionType
    ): Promise<SaveEstimateResponse> => {
        dispatch(updateIsSavingEstimate(true));
        const estimate = updateEstimateFromDesign(customerInfo);
        dispatch(updateCurrentEstimate(estimate));

        const pngImage = await generatePng();
        const estimateImage = ImageUtils.removeDataUrlPrefix(pngImage);

        const lineItems = quoteLineItems;

        const formData = makeSaveEstimateFormData({
            userToken,
            discountOverride,
            customer: customerInfo,
            estimateData: estimate,
            estimateImageBase64: estimateImage,
            lineItems,
            saveAction,
            code: fetchedEstimate?.code,
            ticketNumber: validatedTicketNumber,
            externalId: locationExternalId,
        });

        // POST the estimate and wait for a response
        const resultData = { data: undefined, error: undefined, ...(await postEstimate(formData)) }; //merge the data and error - WTF???

        if (resultData?.error || !resultData.data) {
            dispatch(updateIsSavingEstimate(false));
            handlePostError(resultData.error as AxiosError);
            return Promise.reject(resultData.error);
        }
        const saveResponse = resultData.data;

        if (saveResponse?.estimateNumber) {
            trackEstimateSaved({
                estimateNumber: saveResponse.estimateNumber,
                pricedProducts,
                priceTotals,
            });
            dispatch(setDiscountOverride(undefined));

            showNotification({
                type: NotifierType.SUCCESS,
                title: t('SAVE.RESPONSE.SUCCESS.TITLE'),
                description: t('SAVE.RESPONSE.SUCCESS.DESCRIPTION'),
                message: t('SAVE.RESPONSE.SUCCESS.MESSAGE', { estimateNumber: saveResponse.estimateNumber }),
                duration: 0,
            });

            (saveResponse.messages ?? emptyArray).forEach((message) => {
                // cart message can contain HTML, but limited options
                const safeMessage = sanitize(message.cartMessage ?? '', {
                    allowedTags: ['b', 'i', 'em', 'strong', 'a'],
                    allowedAttributes: {
                        a: ['href', 'name', 'target'],
                    },
                });

                showNotification({
                    type: message.cartStatus ? NotifierType.SUCCESS : NotifierType.WARNING,
                    title: t('SAVE.RESPONSE.CART_MESSAGE.TITLE'),
                    description: t('SAVE.RESPONSE.CART_MESSAGE.DESCRIPTION'),
                    message: t('SAVE.RESPONSE.CART_MESSAGE.MESSAGE', { cartMessage: safeMessage }),
                    duration: 0,
                });
            });

            // add the new code to the history and fetch
            const newUrl = new URL(window.location.toString());
            newUrl.searchParams.set('loadEstimate', saveResponse.code);
            window.history.pushState({ path: newUrl.toString() }, '', newUrl.toString());
            fetchEstimate({ params: { code: saveResponse.code } }).then((result) => {
                if (result?.data) {
                    const currentStatus = new EstimateStatus({
                        status: result?.data?.status,
                        paymentUrl: result?.data?.paymentUrl,
                    });
                    notifyFromStatus({ estimateStatus: currentStatus, fetchedEstimate: result?.data }); // don't care about results, next status check will update
                    dispatch(updateEeStatusList([])); // clear any previous status results

                    //now save it for comparison with a future /estimate/status API update
                    dispatch(updatePrevEstimateStatus(currentStatus));
                }
                dispatch(updateIgnoreFetchedEstimate(result.isError)); // stop ignoring it if successfully fetched (no idea what to do otherwise!)
            });
        } else {
            // the POST is successful, but the back end rejects the save, so no estimateNumber is created
            // This is not a common situation, the email address must be modified in the back end, possibly during editing in EE
            showNotification({
                type: NotifierType.ERROR,
                title: 'Estimate Saved Error',
                description: 'Estimate save error',
                message: 'Customer email does not match existing estimate. Please contact customer support.',
                duration: 0,
            });
            dispatch(updateIsSavingEstimate(false));
        }
        return new Promise((resolve) => resolve(saveResponse));
    };

    useEffect(() => {
        setIsDirtyState(isEstimateDirty);
    }, [isEstimateDirty]);

    return {
        saveEstimate,
        isEstimateDirty,
        getDisplayDates,
        isSaveLoading,
        saveResponse: saveResponse,
    };
}

/**
 * generate a hash of a list of pricedProducts to be used to determine if the estimate is dirty
 * @param products
 */
const pricedProductHash = (products: PricedProduct[] | undefined): number => {
    if (!products?.length) {
        return 0;
    }
    return stringHash(
        products
            .map((product) => stringHash(dirtyProductString(product)))
            .sort()
            .join()
    );
};

const handlePostError = (error: AxiosError) => {
    if (error.response) {
        if (
            error.response.status === 400 &&
            ((error.response.data as ApiErrorResponse)?.errorCode as ErrorCodes) === ErrorCodes.BAD_ESTIMATION
        ) {
            showNotification({
                type: NotifierType.ERROR,
                title: `Error Processing Estimate`,
                description: 'Your estimate has been saved successfully, but was rejected by IME',
                message: 'Please check the portal for updated estimate information',
                duration: 0,
            });
        } else if (error.response.status === 422) {
            const errorResponseData = error.response.data as ApiErrorResponse;

            showNotification({
                type: NotifierType.ERROR,
                title: `Save Estimate Error ${error.response.status ?? ''}`,
                description: errorResponseData.errorMessage,
                message: errorResponseData.validationErrors.map((v: ValidationError) => v.errorMessage).join(', '),
                duration: 0,
            });
        } else {
            showNotification({
                type: NotifierType.ERROR,
                title: 'Save Estimate Error',
                description: 'There was an error when saving this estimate',
                message: error.message,
                duration: 0,
            });
        }
    } else {
        const errorCode = error.code ?? 'UNKNOWN ERROR';
        const errorMessage = error.message ?? 'UNKNOWN ERROR';
        showNotification({
            type: NotifierType.ERROR,
            title: `Save Estimate Error ${errorCode}`,
            description: 'There was an error when saving this estimate',
            message: errorMessage,
            duration: 0,
        });
    }
};
export default useEstimateSaver;
